import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Leaf, TrendingDown, BarChart3, FileSpreadsheet, Bell, Check, ArrowRight,
  Users, Tags, Percent, LineChart, ScanSearch, Upload, ChevronDown,
  ShoppingCart, Scale, BellRing, Utensils, ClipboardList, FileText,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const euro = (c) => `€ ${(c / 100).toFixed(2).replace(".", ",")}`;

const FEATURES = [
  { icon: FileSpreadsheet, title: "Acquisti automatici", text: "Carica le fatture elettroniche XML (FatturaPA): il catalogo prodotti e i prezzi dei fornitori si aggiornano da soli." },
  { icon: LineChart, title: "Andamento prezzi", text: "Segui il prezzo unitario di ogni prodotto nel tempo e individua subito i trend in salita." },
  { icon: ScanSearch, title: "Confronto fornitori", text: "Scopri chi ti fa il prezzo migliore su ogni articolo, con media, minimo e ultimo prezzo." },
  { icon: Bell, title: "Avvisi intelligenti", text: "Alert automatici quando un prodotto aumenta o supera il tuo prezzo obiettivo, anche via email." },
  { icon: Percent, title: "Marginalità & food cost", text: "Margini per prodotto e reparto calcolati in automatico, con ordinamento per rischio." },
  { icon: Tags, title: "Reparti a colori", text: "Categorie personalizzate con colori e assegnazione automatica per parole chiave." },
  { icon: Upload, title: "Vendite da Excel", text: "Importa le vendite da un file Excel e misura l'incidenza reale dei costi sul fatturato." },
  { icon: Users, title: "Team & report", text: "Invita il tuo team e ricevi riepiloghi PDF/Excel a colori e digest via email." },
  { icon: Scale, title: "Prezzo reale al kg e al litro", text: "Gestisci prodotti a confezione, a peso o a volume: QCOST calcola il prezzo per unità (€/kg, €/l) e confronta anche fornitori con unità diverse." },
  { icon: ShoppingCart, title: "Ordini fornitori", text: "Crea ordini di acquisto dalla cambusa con andamento prezzi a 6 mesi, PDF automatico ed email al fornitore. (piani Pro e Business)" },
  { icon: BellRing, title: "Avvisi in-app", text: "Una campanella raccoglie aumenti di prezzo, superamenti obiettivo e prodotti sotto margine, con badge dei nuovi avvisi." },
  { icon: Utensils, title: "Food cost per porzione", text: "Imposta la resa/porzione e ottieni il costo del piatto partendo dal prezzo al kg, per prezzare i menu con margini corretti." },
  { icon: ClipboardList, title: "Ricette e food cost per piatto", text: "Componi i piatti con gli ingredienti dall'anagrafica: costo primario in tempo reale, sotto-ricette, food cost % e margine per porzione, anche al netto IVA. (piano Business)" },
  { icon: FileText, title: "Listino menu & scheda piatto", text: "Genera il listino menu in PDF e la scheda stampabile di ogni piatto con dosi, costi e food cost da appendere in cucina. (piano Business)" },
];

const SHOWCASES = [
  {
    img: "/shots/dashboard.png", eyebrow: "Panoramica",
    title: "Tutti i numeri del locale, a colpo d'occhio.",
    text: "Costi, vendite, incidenza food cost e avvisi attivi in un'unica dashboard. Confronti mese su mese e reparti in aumento o in calo, sempre aggiornati.",
    points: ["Costi vs Vendite per mese", "Incidenza costi sul fatturato", "Aumenti recenti in evidenza"],
  },
  {
    img: "/shots/avvisi.png", eyebrow: "Avvisi prezzi",
    title: "Nessun aumento ti sfugge più.",
    text: "Ogni volta che un fornitore ritocca i prezzi lo vedi subito, con la variazione percentuale e il confronto con il tuo prezzo obiettivo. Filtra per reparto in un tocco.",
    points: ["Aumenti rispetto all'acquisto precedente", "Soglia prezzo obiettivo per prodotto", "Filtri per reparto a colori"],
  },
  {
    img: "/shots/andamento.png", eyebrow: "Andamento prezzi",
    title: "Lo storico prezzi di ogni prodotto.",
    text: "Seleziona uno o più articoli e confronta l'andamento del prezzo unitario nel tempo. Capisci quando conviene fare scorta e quando cambiare fornitore.",
    points: ["Grafico prezzo unitario nel tempo", "Confronto tra più prodotti", "Variazione dal primo acquisto"],
  },
  {
    img: "/shots/acquisti.png", eyebrow: "Acquisti",
    title: "Dalla fattura XML al catalogo, in automatico.",
    text: "Trascina le fatture elettroniche: QCOST estrae righe, fornitori e prezzi, scarta le righe descrittive e popola l'Anagrafica Prodotti senza inserimenti manuali.",
    points: ["Import massivo FatturaPA", "Filtri righe da scartare", "Anagrafica sempre aggiornata"],
  },
  {
    img: "/shots/marginalita.png", eyebrow: "Marginalità",
    title: "I margini reali, reparto per reparto.",
    text: "QCOST calcola margine percentuale e assoluto di ogni prodotto in vendita e li raggruppa per reparto a colori. Ordina per rischio e individua subito ciò che erode il tuo profitto.",
    points: ["Margine % e assoluto per prodotto", "Grafico margine medio per reparto", "Avvisi sui prodotti sotto soglia"],
  },
  {
    img: "/shots/andamento.png", eyebrow: "Prezzo per unità",
    title: "Confronta prezzi anche a peso e a volume.",
    text: "Per ogni fornitore scegli l'unità di misura (pezzo, kg, g, l, cl, ml) e la quantità: QCOST calcola il prezzo per unità e normalizza in automatico (grammi ↔ kg, ml ↔ litri) per confrontare offerte davvero diverse.",
    points: ["Prezzo reale al kg / al litro", "Confronto cross-unità tra fornitori", "Andamento del prezzo per unità nel tempo"],
  },
  {
    img: "/shots/acquisti.png", eyebrow: "Ordini fornitori",
    title: "Dalla cambusa all'ordine, in un clic.",
    text: "Componi l'ordine scegliendo dalla cambusa con l'andamento prezzi a 6 mesi, imposta la data di consegna e invia: QCOST genera il PDF numerato e lo spedisce via email al fornitore e a te. (piani Pro e Business)",
    points: ["Andamento prezzi a 6 mesi per fornitore", "PDF ordine numerato automatico", "Invio email a fornitore e titolare"],
  },
];

const COMPARISON = [
  { label: "Utenti inclusi", value: (p) => `${p.max_users}` },
  { label: "Import fatture FatturaPA" },
  { label: "Vendite da Excel & incidenza costi" },
  { label: "Marginalità e food cost" },
  { label: "Prezzo reale per confezione, peso e volume" },
  { label: "Food cost per porzione" },
  { label: "Avvisi in-app ed email" },
  { label: "Report PDF/Excel a colori" },
  { label: "Modulo Ordini fornitori / Acquisti (PDF + email)", plans: [false, true, true] },
  { label: "Modulo Ricette — food cost per piatto, sotto-ricette, listino menu", plans: [false, false, true] },
];

const STEPS = [
  { n: "1", title: "Carica le fatture", text: "Trascina le fatture elettroniche XML o inseriscile a mano. Bastano pochi secondi." },
  { n: "2", title: "Importa le vendite", text: "Carica il file Excel delle vendite: QCOST calcola margini e incidenza costi." },
  { n: "3", title: "Ottimizza i margini", text: "Ricevi avvisi sugli aumenti e agisci sui prodotti che erodono la marginalità." },
];

const FAQS = [
  { q: "Devo inserire una carta di credito per provare QCOST?", a: "No. La prova gratuita di 15 giorni non richiede alcuna carta: crei l'account e inizi subito. Deciderai se abbonarti solo al termine del periodo di prova." },
  { q: "Che formato di fatture posso caricare?", a: "QCOST legge le fatture elettroniche in formato XML FatturaPA, lo standard italiano. Puoi caricarne più di una alla volta oppure inserire una fattura manualmente." },
  { q: "Come vengono calcolati i margini?", a: "Per ogni prodotto in vendita QCOST confronta il miglior prezzo d'acquisto tra i tuoi fornitori con il prezzo di vendita, calcolando margine percentuale e assoluto, e li raggruppa per reparto a colori." },
  { q: "Gestisce prodotti venduti a peso o a volume?", a: "Sì. Per ogni fornitore puoi indicare l'unità di misura (pezzo, kg, g, l, cl, ml) e la quantità per riga fattura: QCOST calcola il prezzo per unità (es. €/kg) e normalizza automaticamente grammi/kg e ml/cl/l, così puoi confrontare anche fornitori con unità diverse. Dalla descrizione della fattura propone anche la quantità rilevata da confermare." },
  { q: "Posso creare gli ordini ai fornitori?", a: "Con i piani Pro e Business hai il modulo Ordini fornitori: componi l'ordine dalla cambusa con l'andamento prezzi a 6 mesi, imposti la data di consegna e QCOST genera un PDF numerato inviandolo via email al fornitore e a te." },
  { q: "C'è la gestione delle ricette e del food cost per piatto?", a: "Sì, con il piano Business hai il modulo Ricette: componi ogni piatto con gli ingredienti dall'anagrafica, il costo primario si aggiorna da solo coi prezzi d'acquisto, gestisci le sotto-ricette e ottieni food cost % e margine (anche al netto IVA), oltre al listino menu in PDF e alla scheda stampabile di ogni piatto." },
  { q: "Posso invitare i miei collaboratori?", a: "Sì. In base al piano scelto puoi aggiungere più utenti al tuo team, ciascuno con accesso ai dati della tua azienda." },
  { q: "I miei dati sono al sicuro?", a: "Ogni azienda vede solo i propri dati, isolati dagli altri account. Le informazioni sono trattate in modo riservato e accessibili solo agli utenti autorizzati." },
  { q: "Posso disdire quando voglio?", a: "Certo, nessun vincolo: puoi cambiare piano o interrompere l'abbonamento in qualsiasi momento dalla sezione Abbonamento." },
];

const Shot = ({ src, alt }) => (
  <div className="rounded-lg overflow-hidden border border-stone-200 shadow-2xl shadow-stone-900/10 bg-white transition-transform duration-500 hover:-translate-y-1">
    <div className="flex items-center gap-1.5 px-3.5 py-2.5 border-b border-stone-100 bg-stone-50">
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#8B3A3A" }} />
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#D4AF37" }} />
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#6B8E7A" }} />
    </div>
    <img src={src} alt={alt} loading="lazy" className="w-full block" />
  </div>
);

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="landing-faq" className="border-b border-stone-200">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-4 py-5 text-left group">
        <span className="font-serif text-xl text-stone-900 group-hover:text-brand transition-colors">{q}</span>
        <ChevronDown size={20} className={`text-brand shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? "max-h-56 pb-5" : "max-h-0"}`}>
        <p className="text-sm text-stone-600 leading-relaxed pr-8">{a}</p>
      </div>
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [stripeReady, setStripeReady] = useState(true);

  useEffect(() => { if (user) navigate("/", { replace: true }); }, [user, navigate]);
  useEffect(() => {
    fetch(`${API}/api/billing/plans`).then((r) => r.json()).then(setPlans).catch(() => {});
    fetch(`${API}/api/billing/config`).then((r) => r.json()).then((d) => setStripeReady(!!d.publishable_key)).catch(() => setStripeReady(false));
  }, []);

  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("is-visible"); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -6% 0px" });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [plans]);

  useEffect(() => {
    const ld = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question", name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "faq-jsonld";
    s.text = JSON.stringify(ld);
    document.head.appendChild(s);
    return () => { const e = document.getElementById("faq-jsonld"); if (e) e.remove(); };
  }, []);

  const goRegister = () => navigate("/login?mode=register");

  return (
    <div className="min-h-screen bg-[#FAFAF9] text-stone-900">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#FAFAF9]/80 border-b border-stone-200/70">
        <div className="flex items-center justify-between px-6 sm:px-10 py-4 max-w-6xl mx-auto">
          <div className="flex items-center gap-2 font-serif text-2xl"><Leaf size={22} className="text-brand" /> QCOST</div>
          <div className="flex items-center gap-3">
            <button data-testid="landing-login-btn" onClick={() => navigate("/login")} className="text-sm font-semibold text-stone-600 hover:text-stone-900 transition-colors">Accedi</button>
            <button data-testid="landing-cta-top" onClick={goRegister} className="rounded-full bg-brand text-white px-4 py-2 text-sm font-semibold hover:bg-brand-hover transition-colors">Inizia gratis</button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 pt-16 pb-14 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand mb-4">Controllo costi per bar & ristoranti</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">Meno sprechi, <span className="text-brand">più margine</span> su ogni piatto.</h1>
          <p className="text-base text-stone-600 mt-6 max-w-lg">QCOST legge le fatture elettroniche dei tuoi fornitori, monitora gli aumenti di prezzo e calcola la marginalità reale di ogni prodotto. Tutto in automatico.</p>
          <div className="flex flex-wrap items-center gap-4 mt-8">
            <button data-testid="landing-cta-hero" onClick={goRegister} className="rounded-full bg-brand text-white px-7 py-3.5 text-sm font-semibold flex items-center gap-2 hover:bg-brand-hover transition-colors">Inizia la prova gratuita <ArrowRight size={16} /></button>
            <span className="text-sm text-stone-500">15 giorni gratis · nessuna carta richiesta</span>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2 mt-8 text-sm text-stone-500">
            <span className="flex items-center gap-1.5"><Check size={15} className="text-brand" /> Import FatturaPA</span>
            <span className="flex items-center gap-1.5"><Check size={15} className="text-brand" /> Report PDF/Excel</span>
            <span className="flex items-center gap-1.5"><Check size={15} className="text-brand" /> Avvisi via email</span>
          </div>
        </div>
        <div className="relative">
          <Shot src="/shots/dashboard.png" alt="Dashboard QCOST" />
          <div className="hidden sm:block absolute -bottom-5 -left-5 bg-white rounded-sm border border-stone-200 shadow-xl px-4 py-3">
            <p className="text-[10px] uppercase tracking-wider font-bold text-stone-400">Incidenza food cost</p>
            <p className="font-serif text-2xl text-brand">4,1%</p>
          </div>
          <div className="hidden sm:block absolute -top-5 -right-4 bg-brand text-white rounded-sm shadow-xl px-4 py-3 noise-overlay">
            <p className="text-[10px] uppercase tracking-wider font-bold text-white/70">Aumento rilevato</p>
            <p className="font-serif text-2xl">+12%</p>
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section className="bg-white border-y border-stone-200 py-20">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <div className="max-w-2xl mb-12 reveal">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand mb-3">Tutto quello che ti serve</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light leading-tight">Un'unica piattaforma per costi, prezzi e marginalità.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {FEATURES.map((b, i) => (
              <div key={b.title} data-testid="landing-feature" className="group reveal" style={{ transitionDelay: `${(i % 4) * 70}ms` }}>
                <div className="w-11 h-11 rounded-full bg-brand/10 text-brand flex items-center justify-center mb-4 transition-colors group-hover:bg-brand group-hover:text-white"><b.icon size={20} /></div>
                <h3 className="font-serif text-2xl mb-2">{b.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SHOWCASES */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 py-24 space-y-24">
        {SHOWCASES.map((s, i) => (
          <div key={s.title} data-testid="landing-showcase" className="grid lg:grid-cols-2 gap-12 items-center reveal">
            <div className={i % 2 === 1 ? "lg:order-2" : ""}>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand mb-3">{s.eyebrow}</p>
              <h3 className="font-serif text-3xl sm:text-4xl font-light leading-tight">{s.title}</h3>
              <p className="text-base text-stone-600 mt-4 leading-relaxed">{s.text}</p>
              <ul className="space-y-2.5 mt-6">
                {s.points.map((p) => (
                  <li key={p} className="flex items-center gap-2.5 text-sm text-stone-700"><span className="w-5 h-5 rounded-full bg-brand/10 text-brand flex items-center justify-center shrink-0"><Check size={13} /></span> {p}</li>
                ))}
              </ul>
            </div>
            <div className={i % 2 === 1 ? "lg:order-1" : ""}>
              <Shot src={s.img} alt={s.eyebrow} />
            </div>
          </div>
        ))}
      </section>

      {/* ORDINI DEEP DIVE */}
      <section className="bg-white border-y border-stone-200 py-24">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand mb-3">Ordini fornitori · Pro & Business</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light leading-tight">Ordina meglio, con lo storico prezzi sott'occhio.</h2>
            <p className="text-base text-stone-600 mt-4 leading-relaxed">Scegli i prodotti dalla cambusa vedendo l'andamento prezzi a 6 mesi, imposta quantità e data di consegna. QCOST genera un ordine PDF numerato e lo invia via email al fornitore e a te.</p>
            <ul className="grid sm:grid-cols-2 gap-3 mt-6">
              {["Andamento prezzi a 6 mesi", "Numerazione progressiva", "PDF professionale automatico", "Email a fornitore e titolare", "Data di consegna", "Prezzo per unità (€/kg, €/l)"].map((x) => (
                <li key={x} className="flex items-center gap-2 text-sm text-stone-700"><span className="w-5 h-5 rounded-full bg-brand/10 text-brand flex items-center justify-center shrink-0"><Check size={13} /></span>{x}</li>
              ))}
            </ul>
          </div>
          <div className="reveal">
            <div className="relative mx-auto max-w-sm">
              <div data-testid="order-pdf-demo" className="bg-white rounded-lg border border-stone-200 shadow-2xl shadow-stone-900/10 p-6 rotate-[-1.5deg] transition-transform duration-500 hover:rotate-0">
                <div className="flex items-center justify-between border-b border-stone-200 pb-3">
                  <div className="flex items-center gap-2 font-serif text-xl"><Leaf size={18} className="text-brand" /> QCOST</div>
                  <div className="text-right"><p className="text-[9px] uppercase tracking-wider text-stone-400 font-bold">Ordine</p><p className="font-serif text-lg text-brand">N° 000124</p></div>
                </div>
                <div className="flex justify-between text-[11px] text-stone-500 mt-3">
                  <div><p className="font-bold text-stone-700">Fornitore</p><p>Ortofrutta Bianchi Srl</p></div>
                  <div className="text-right"><p className="font-bold text-stone-700">Consegna</p><p>Ven 12/07</p></div>
                </div>
                <table className="w-full text-[11px] mt-4">
                  <thead><tr className="text-stone-400 border-b border-stone-200"><th className="text-left py-1 font-semibold">Articolo</th><th className="text-center font-semibold">Q.tà</th><th className="text-right font-semibold">Prezzo</th></tr></thead>
                  <tbody className="text-stone-700">
                    <tr className="border-b border-stone-100"><td className="py-1.5">Pomodoro pelato</td><td className="text-center">6</td><td className="text-right">€ 8,40</td></tr>
                    <tr className="border-b border-stone-100"><td className="py-1.5">Mozzarella fiordilatte</td><td className="text-center">4</td><td className="text-right">€ 21,20</td></tr>
                    <tr className="border-b border-stone-100"><td className="py-1.5">Olio EVO 5 l</td><td className="text-center">2</td><td className="text-right">€ 47,00</td></tr>
                  </tbody>
                </table>
                <div className="flex justify-between items-center mt-4 pt-3 border-t border-stone-200">
                  <span className="text-[10px] uppercase tracking-wider text-stone-400 font-bold">Totale</span>
                  <span className="font-serif text-2xl text-brand">€ 76,60</span>
                </div>
              </div>
              <div className="hidden sm:flex absolute -bottom-4 -right-3 items-center gap-1.5 bg-brand text-white rounded-full shadow-xl px-3 py-1.5 text-xs font-semibold noise-overlay"><FileSpreadsheet size={13} /> PDF inviato</div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white border-y border-stone-200 py-20">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <div className="text-center mb-14 reveal">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand mb-3">Come funziona</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light">Operativo in tre passi.</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-8">
            {STEPS.map((st, i) => (
              <div key={st.n} className="relative rounded-sm border border-stone-200 p-8 reveal" style={{ transitionDelay: `${i * 90}ms` }}>
                <span className="absolute -top-4 left-8 w-9 h-9 rounded-full bg-brand text-white font-serif text-lg flex items-center justify-center noise-overlay">{st.n}</span>
                <h3 className="font-serif text-2xl mt-3 mb-2">{st.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{st.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 py-24">
        <div className="text-center mb-12 reveal">
          <h2 className="font-serif text-4xl sm:text-5xl font-light">Piani semplici e trasparenti</h2>
          <p className="text-stone-600 mt-3">Inizia con 15 giorni di prova gratuita, poi scegli il piano su misura per te.</p>
          {!stripeReady && (
            <p data-testid="landing-stripe-note" className="text-sm text-brand bg-brand/10 border border-brand/20 rounded-full px-4 py-2 mt-4 inline-block">
              I pagamenti online sono in fase di attivazione: puoi comunque iniziare subito la prova gratuita.
            </p>
          )}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((p, i) => (
            <div key={p.key} data-testid={`landing-plan-${p.key}`} className={`rounded-sm border p-8 flex flex-col transition-transform duration-300 hover:-translate-y-1 ${i === 1 ? "border-brand bg-white shadow-lg" : "border-stone-200 bg-white"}`}>
              {i === 1 && <span className="self-start text-[10px] uppercase tracking-wider font-bold text-white bg-brand px-2 py-0.5 rounded-full mb-3">Più scelto</span>}
              <h3 className="font-serif text-3xl">{p.name}</h3>
              <p className="font-serif text-4xl font-light text-brand mt-3">{euro(p.price)}<span className="text-sm text-stone-400">/mese</span></p>
              <p className="text-xs text-stone-500 mt-1">o {euro(p.price_annual)}/anno {p.annual_savings_eur > 0 ? `· risparmi ${p.annual_savings_eur.toFixed(0)} €` : ""}</p>
              <ul className="space-y-2 mt-6 text-sm text-stone-600 flex-1">
                <li className="flex items-center gap-2"><Check size={15} className="text-brand shrink-0" /> Fino a {p.max_users} utenti</li>
                <li className="flex items-center gap-2"><Check size={15} className="text-brand shrink-0" /> Import FatturaPA, Vendite e Marginalità</li>
                <li className="flex items-center gap-2"><Check size={15} className="text-brand shrink-0" /> Prezzo reale per confezione, peso e volume (€/kg, €/l)</li>
                <li className="flex items-center gap-2"><Check size={15} className="text-brand shrink-0" /> Food cost per porzione e costo del piatto</li>
                <li className="flex items-center gap-2"><Check size={15} className="text-brand shrink-0" /> Avvisi in-app ed email su aumenti e margini bassi</li>
                <li className="flex items-center gap-2"><Check size={15} className="text-brand shrink-0" /> Report PDF/Excel con prezzo confezione e per unità</li>
                {i >= 1 && <li className="flex items-center gap-2 font-semibold text-stone-700"><Check size={15} className="text-brand shrink-0" /> Modulo Ordini fornitori (PDF + email)</li>}
                {i >= 2 && <li className="flex items-center gap-2 font-semibold text-stone-700"><Check size={15} className="text-brand shrink-0" /> Modulo Ricette (food cost per piatto, listino menu)</li>}
              </ul>
              <button data-testid={`landing-plan-cta-${p.key}`} onClick={goRegister} className={`mt-8 rounded-full px-5 py-3 text-sm font-semibold transition-colors ${i === 1 ? "bg-brand text-white hover:bg-brand-hover" : "border border-brand text-brand hover:bg-brand hover:text-white"}`}>Inizia la prova gratuita</button>
            </div>
          ))}
        </div>
      </section>

      {/* COMPARISON */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 pb-24">
        <div className="text-center mb-10 reveal">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand mb-3">Confronto piani</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-light">Cosa sblocca ogni livello</h2>
        </div>
        {plans.length > 0 && (
          <div className="overflow-x-auto reveal">
            <table data-testid="plan-comparison" className="w-full min-w-[600px] border-collapse">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="text-left py-4 pr-4 font-serif text-lg text-stone-800">Funzionalità</th>
                  {plans.map((p, i) => (
                    <th key={p.key} className={`py-4 px-4 text-center font-serif text-lg ${i === 1 ? "text-brand" : "text-stone-800"}`}>{p.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-sm">
                {COMPARISON.map((row) => (
                  <tr key={row.label} className="border-b border-stone-100">
                    <td className="py-3 pr-4 text-stone-700">{row.label}</td>
                    {plans.map((p, i) => {
                      const v = row.value ? row.value(p) : (row.plans ? row.plans[i] : true);
                      return (
                        <td key={p.key} className={`py-3 px-4 text-center ${i === 1 ? "bg-brand/5" : ""}`}>
                          {typeof v === "string"
                            ? <span className="font-semibold text-stone-800">{v}</span>
                            : (v ? <Check size={16} className="text-brand inline" /> : <span className="text-stone-300">—</span>)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* FAQ */}
      <section className="bg-white border-y border-stone-200 py-20">
        <div className="max-w-3xl mx-auto px-6 sm:px-10">
          <div className="text-center mb-10 reveal">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand mb-3">Domande frequenti</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light">Hai ancora qualche dubbio?</h2>
          </div>
          <div className="reveal">
            {FAQS.map((f) => <FaqItem key={f.q} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand text-white py-20 noise-overlay">
        <div className="max-w-3xl mx-auto px-6 text-center reveal">
          <TrendingDown size={40} className="mx-auto mb-5 text-white/80" />
          <h2 className="font-serif text-4xl sm:text-5xl font-light">Pronto a tagliare i costi?</h2>
          <p className="text-white/80 mt-3">Crea il tuo account in un minuto. 15 giorni di prova, senza carta.</p>
          <button data-testid="landing-cta-bottom" onClick={goRegister} className="mt-8 rounded-full bg-white text-brand px-7 py-3.5 text-sm font-semibold hover:bg-stone-100 transition-colors inline-flex items-center gap-2">Inizia la prova gratuita <ArrowRight size={16} /></button>
        </div>
      </section>

      <footer className="border-t border-stone-200 py-10">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2 font-serif text-xl text-stone-800"><Leaf size={18} className="text-brand" /> QCOST</div>
          <div className="text-sm text-stone-500 sm:text-right space-y-0.5">
            <p className="font-semibold text-stone-700">Vivi Verde Srl</p>
            <p>Piazza del Popolo, 18 — 00187 Roma · P.Iva 01454640556</p>
            <p><a href="mailto:vivi.verdesrl@icloud.com" className="text-brand hover:underline">vivi.verdesrl@icloud.com</a></p>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 sm:px-10 mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p className="text-xs text-stone-400">© {new Date().getFullYear()} QCOST — Controllo costi per la ristorazione. Tutti i diritti riservati.</p>
          <div className="flex items-center gap-4 text-xs">
            <button data-testid="footer-privacy" onClick={() => navigate("/privacy-policy")} className="text-stone-500 hover:text-brand transition-colors">Privacy Policy</button>
            <span className="text-stone-300">·</span>
            <button data-testid="footer-cookie" onClick={() => navigate("/cookie-policy")} className="text-stone-500 hover:text-brand transition-colors">Cookie Policy</button>
            <span className="text-stone-300">·</span>
            <button data-testid="footer-terms" onClick={() => navigate("/termini")} className="text-stone-500 hover:text-brand transition-colors">Termini e Condizioni</button>
            <span className="text-stone-300">·</span>
            <button data-testid="footer-cookie-prefs" onClick={() => window.dispatchEvent(new Event("qcost:open-cookie"))} className="text-stone-500 hover:text-brand transition-colors">Preferenze cookie</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
